import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateCountryRequest } from '@/types/model';
import { useTranslation } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import ImageNotAvailable from '@/assets/image-not-available.png';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { MediaSource } from '@/types/media';

const createCountrySchema = (t: Translation) => z.object({
  nameEnglish: z
    .string()
    .min(1, t.countries.validation.nameEnglishRequired)
    .max(150, t.countries.validation.nameMax),
  name: z
    .string()
    .min(1, t.countries.validation.nameRequired)
    .max(150, t.countries.validation.nameMax),
  flag: z
    .string()
    .optional()
    .or(z.literal('')),
});

type CountryFormData = z.infer<ReturnType<typeof createCountrySchema>>;

interface CountryFormProps {
  onSuccess?: (data: CreateCountryRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateCountryRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const CountryForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: CountryFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue
  } = useForm<CountryFormData>({
    resolver: zodResolver(createCountrySchema(t)),
    defaultValues: {
      nameEnglish: initialData?.nameEnglish || '',
      name: initialData?.name || '',
      flag: initialData?.flag || '',
    }
  });

  const flagValue = watch('flag') || '';

  const onSubmit = async (data: CountryFormData) => {
    setSubmitError(null);

    try {
      await onSuccess?.({
        name: data.name,
        nameEnglish: data.nameEnglish,
        flag: data.flag || undefined,
      });
      if (!isEdit) {
        reset({
          nameEnglish: '',
          name: '',
          flag: ''
        });
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const renderFlagPreview = () => {
    if (!flagValue) {
      return (
        <div className="h-40 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-500 text-xs">
            {t.countries.placeholders.flagPreview}
          </div>
        </div>
      );
    }

    const isUrl = /^https?:\/\//.test(flagValue);

    if (!isUrl) {
      return (
        <div className="h-40 w-40 border border-gray-200 rounded-lg flex items-center justify-center bg-white text-5xl">
          {flagValue}
        </div>
      );
    }

    return (
      <img
        src={flagValue}
        alt={t.countries.fields.flag}
        className="h-40 w-40 object-cover rounded-lg shadow-md"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = ImageNotAvailable;
        }}
      />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? t.countries.editTitle : t.countries.createTitle}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? t.countries.editDescription : t.countries.createDescription}
        </p>
      </div>

      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t.countries.fields.name}
              placeholder={t.countries.placeholders.name}
              error={errors.name?.message}
              maxLength={150}
              {...register('name')}
            />
            <Input
              label={t.countries.fields.nameEnglish}
              placeholder={t.countries.placeholders.nameEnglish}
              error={errors.nameEnglish?.message}
              maxLength={150}
              {...register('nameEnglish')}
            />

            <div className="md:col-span-2">
              <ImageUrlUploadField
                label={t.countries.fields.flag}
                placeholder={t.countries.placeholders.flag}
                value={flagValue}
                onChange={(url) => setValue('flag', url, { shouldValidate: true, shouldDirty: true })}
                error={errors.flag?.message}
                disabled={isSubmitting}
                source={MediaSource.COUNTRY_FLAG}
                showPreview={false}
              />
            </div>
          </div>

          <div className="flex-shrink-0">
            {renderFlagPreview()}
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t.countries.actions.creating
              : isEdit
                ? t.countries.actions.update
                : t.countries.actions.create
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CountryForm;
